import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const orig = err.config;
    if (err.response?.status === 401 && !orig._retry && !orig.url.includes("/auth/")) {
      orig._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(orig);
      } catch (e) {
        refreshing = null;
      }
    }
    return Promise.reject(err);
  }
);

export function errMsg(e, fallback = "Etwas ist schiefgelaufen. Bitte erneut versuchen.") {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
}

export const eur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export const fmtDateShort = (d) =>
  d ? new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "short" }) : "—";

export const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "short" }) +
      ", " +
      new Date(d).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : "—";

export const fmtMinutes = (mins) => {
  if (!mins) return "0h";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}min` : `${h}h`;
};

export const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const STATUS = {
  neu: { label: "Neu", color: "#3B82F6" },
  planung: { label: "Planung", color: "#8B5CF6" },
  in_arbeit: { label: "In Arbeit", color: "#B88A4A" },
  wartet_kunde: { label: "Wartet auf Kunde", color: "#F59E0B" },
  aenderungen: { label: "Änderungen angefragt", color: "#F97316" },
  review: { label: "Review", color: "#A855F7" },
  abgeschlossen: { label: "Abgeschlossen", color: "#22C55E" },
  archiviert: { label: "Archiviert", color: "#6B7280" },
};
export const STATUS_ORDER = Object.keys(STATUS);

export const TASK_STATUS = {
  todo: { label: "Zu erledigen", color: "#6B7280" },
  in_arbeit: { label: "In Arbeit", color: "#B88A4A" },
  wartend: { label: "Wartend", color: "#F59E0B" },
  fertig: { label: "Fertig", color: "#22C55E" },
};

export const PRIORITY = {
  niedrig: { label: "Niedrig", color: "#6B7280" },
  normal: { label: "Normal", color: "#3B82F6" },
  hoch: { label: "Hoch", color: "#F59E0B" },
  dringend: { label: "Dringend", color: "#EF4444" },
};

export const PAY_STATUS = {
  offen: { label: "Offen", color: "#F59E0B" },
  teilweise: { label: "Teilweise bezahlt", color: "#3B82F6" },
  bezahlt: { label: "Bezahlt", color: "#22C55E" },
  storniert: { label: "Storniert", color: "#EF4444" },
  keine: { label: "Keine Zahlung", color: "#9CA3AF" },
};

export const CATEGORIES = {
  wordpress: "WordPress",
  shopify: "Shopify",
  woocommerce: "WooCommerce",
  website: "Website",
  webshop: "Webshop",
  landingpage: "Landing Page",
  seo: "SEO",
  wartung: "Wartung",
  sonstiges: "Sonstiges",
};

export const PRICING = {
  fixed: "Festpreis",
  hourly: "Stundensatz",
  per_project: "Pro Projekt",
  custom: "Individuell",
};

export const CRED_TYPES = {
  wordpress: "WordPress",
  hosting: "Hosting",
  domain: "Domain",
  ftp: "FTP",
  sftp: "SFTP",
  cpanel: "cPanel",
  plesk: "Plesk",
  shopify: "Shopify",
  woocommerce: "WooCommerce",
  email: "E-Mail",
  google: "Google",
  social: "Social Media",
  other: "Sonstiges",
};

export const PAY_METHODS = {
  ueberweisung: "Überweisung",
  paypal: "PayPal",
  bar: "Bar",
  other: "Sonstiges",
};

export const FILE_FOLDERS = [
  "01 Kundendateien",
  "02 Logos",
  "03 Bilder",
  "04 Texte",
  "05 Website",
  "06 Final",
  "07 Dokumente",
];

export const ACTION_LABELS = {
  login: "Anmeldung",
  project_created: "Projekt erstellt",
  project_updated: "Projekt bearbeitet",
  project_deleted: "Projekt gelöscht",
  status_change: "Status geändert",
  task_created: "Aufgabe erstellt",
  task_updated: "Aufgabe bearbeitet",
  task_completed: "Aufgabe abgeschlossen",
  task_status: "Aufgabenstatus",
  task_deleted: "Aufgabe gelöscht",
  comment_added: "Kommentar",
  file_uploaded: "Datei hochgeladen",
  file_deleted: "Datei gelöscht",
  credential_added: "Zugang angelegt",
  credential_updated: "Zugang aktualisiert",
  credential_revealed: "Zugang angezeigt",
  credential_deleted: "Zugang gelöscht",
  payment_added: "Zahlung eingetragen",
  payment_updated: "Zahlung bearbeitet",
  payment_deleted: "Zahlung gelöscht",
  payment_completed: "Vollständig bezahlt",
  time_tracked: "Zeit erfasst",
  client_created: "Kunde angelegt",
  client_updated: "Kunde bearbeitet",
  client_deleted: "Kunde gelöscht",
  password_change: "Passwort geändert",
  password_reset: "Passwort zurückgesetzt",
  settings_updated: "Einstellungen",
  call_started: "Videoanruf",
};

export const NOTIF_TYPES = [
  { key: "new_project", label: "Projekt-Zuweisungen" },
  { key: "new_task", label: "Aufgaben-Zuweisungen" },
  { key: "new_comment", label: "Neue Kommentare" },
  { key: "mention", label: "Erwähnungen (@)" },
  { key: "file_upload", label: "Datei-Uploads" },
  { key: "payment", label: "Zahlungen" },
  { key: "status_change", label: "Status-Änderungen" },
  { key: "deadline", label: "Fristen-Erinnerungen" },
];
